import {
    forwardRef,
    InputHTMLAttributes,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { IconBaseProps } from "react-icons";
import styled from "styled-components";

export const SemvInputMessageError = styled.p`
    font-size: 13px;
    color: red;

    margin-bottom: 5px;
`;

export const SemvInputLabelContainer = styled.div`
    position: absolute;
    margin-left: 41px;
    margin-bottom: 45px;
    background-color: white;
    height: 15px;
    font-size: 15px;
    color: #234e52;
`;

export const SemvInputContainer = styled.div`
    border-radius: 1.5px;
    padding: 8px;
    color: #333;
    width: 100%;
    height: 52px;
    display: flex;
    align-items: center;

    input {
        flex: 1;
        height: 100%;
        min-height: 40px;
        background: transparent;
        border: 1px solid #234e52;
        border-radius: 6px;
        color: #333;
        padding-left: 6.5px;
        outline: none;

        &::placeholder {
            color: #777;
            font-size: 15px;
        }

        &:focus-within {
            box-shadow: 0 0 3px 0px #126b67;
            border-color: white;
            outline: none;
        }
    }

    svg {
        color: #234e52;
        margin-right: 16px;
    }
`;

export interface SemvInputProps extends InputHTMLAttributes<HTMLInputElement> {
    name: string;
    label?: string;
    icon?: React.ComponentType<IconBaseProps>;
}

export type SemvInputHandle = {
    setError: (error: string) => void;
    getValue: () => string | undefined | null;
};

const SemvInput = forwardRef<SemvInputHandle, SemvInputProps>(
    ({ label, icon: Icon, className, onFocus, ...props }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const [error, setError] = useState<string>();

        useImperativeHandle(ref, () => ({
            setError: (err) => {
                setError(err);
            },
            getValue: () => inputRef.current?.value,
        }));

        return (
            <SemvInputContainer
                style={{
                    display: `${props.hidden ? "none" : "flex"}`,
                }}
                className={className}
            >
                {(label || error) && (
                    <SemvInputLabelContainer>
                        {error ? (
                            <SemvInputMessageError>
                                {error}
                            </SemvInputMessageError>
                        ) : (
                            <p className="text-sm text-teal-800">{label}</p>
                        )}
                    </SemvInputLabelContainer>
                )}
                {Icon && <Icon size={20} color={error && "red"} />}

                <input
                    ref={inputRef}
                    {...props}
                    onFocus={(e) => {
                        setError(undefined);
                        if (onFocus) onFocus(e);
                    }}
                />
            </SemvInputContainer>
        );
    }
);

SemvInput.displayName = "SemvInput";

export { SemvInput };
